<template>
  <div 
    class="radial-menu-wrapper" 
    @mouseleave="startClearTimer" 
    @mouseover="cancelClearTimer"
  >
    
    <!-- CENTER INFO BOX -->
    <div class="center-info-box">
      <div v-if="activeItem">
        <h2 class="center-title">{{ activeItem.name }}</h2>
        <p class="center-description">{{ activeItem.description }}</p>
      </div>
      <div v-else>
        <h2 class="center-title">Main Menu</h2>
        <p class="center-description">Hover over an option</p>
      </div>
    </div>

    <!-- MENU ITEMS CONTAINER -->
    <div class="menu-items-container">
      <!-- Main Menu Items -->
      <router-link
        v-for="item in menuItems"
        :key="item.id"
        :to="item.mainRoute"
        class="menu-item"
        :style="item.style"
        @mouseover="setActiveItem(item)"
      >
        <div class="item-content">
          <img :src="item.icon" :alt="item.name" class="item-icon" />
        </div>
      </router-link>

      <!-- Sub-Menu Items -->
      <transition-group name="pop-out" v-if="activeItem">
        <router-link
          v-for="(subItem, index) in activeItem.subItems"
          :key="subItem.name"
          :to="subItem.route"
          class="sub-item"
          :style="subItem.style"
        >
          <span class="sub-item-label">{{ subItem.name }}</span>
        </router-link>
      </transition-group>
    </div>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const activeItemId = ref(null);
let leaveTimer = null; // This timer is the key to the "grace period"

// --- JAVASCRIPT-POWERED LAYOUT LOGIC ---
const MAIN_RADIUS = 240;
// FIX #2: Increased radius to create a visual gap
const SUB_RADIUS = 340; 
const SUB_ARC_ANGLE = 90;
const toRadians = (degrees) => degrees * (Math.PI / 180);

const menuItems = computed(() => {
  const baseItems = [
    { id: 'onboarding', name: 'Onboarding', description: 'Employee & Project Onboarding', icon: '/images/architect.png', rotation: -60, mainRoute: '/onboarding-forms', subItems: [
      { name: 'Material', route: '/onboarding-material' }, { name: 'Forms', route: '/onboarding-forms' }, { name: 'Records', route: '/admin/form-records' }
    ]},
    { id: 'operatives', name: 'Operatives', description: 'Manage Users & Roles', icon: '/images/worker.png', rotation: 0, mainRoute: '/admin/operatives', subItems: [
      { name: 'Operatives', route: '/admin/operatives' }, { name: 'Roles', route: '#' }, { name: 'Crews', route: '#' }
    ]},
    { id: 'health', name: 'Health & Safety', description: 'HSQE Forms & Fatigue', icon: '/images/helmet.png', rotation: 60, mainRoute: '/health-safety-forms', subItems: [
      { name: 'Fatigue', route: '/fatigue' }, { name: 'Forms', route: '/health-safety-forms' }, { name: 'Records', route: '#' }
    ]},
    { id: 'engineering', name: 'Engineering', description: 'Manage Projects', icon: '/images/teamwork.png', rotation: 120, mainRoute: '#', subItems: [
      { name: 'Projects', route: '#' }, { name: 'Forms', route: '#' }, { name: 'Records', route: '#' }
    ]},
    { id: 'vehicles', name: 'Vehicles', description: 'Manage Fleet', icon: '/images/loading.png', rotation: 180, mainRoute: '#', subItems: [
      { name: 'Vehicle Check', route: '#' }, { name: 'All Vehicles', route: '#' }, { name: 'Records', route: '#' }
    ]},
    { id: 'useful', name: 'Useful Stuff', description: 'Documents & Links', icon: '/images/engagement.png', rotation: 240, mainRoute: '#', subItems: [
      { name: 'Toolbox Talks', route: '#' }, { name: 'Documents', route: '#' }, { name: 'Links', route: '#' }
    ]},
  ];

  return baseItems.map(item => {
    const angleRad = toRadians(item.rotation);
    const x = MAIN_RADIUS * Math.cos(angleRad);
    const y = MAIN_RADIUS * Math.sin(angleRad);

    const subItemsWithPositions = item.subItems.map((subItem, index) => {
        const totalSubItems = item.subItems.length;
        const subAngle = item.rotation + (index - (totalSubItems - 1) / 2) * (SUB_ARC_ANGLE / totalSubItems);
        const subAngleRad = toRadians(subAngle);
        const subX = SUB_RADIUS * Math.cos(subAngleRad);
        const subY = SUB_RADIUS * Math.sin(subAngleRad);
        return {
            ...subItem,
            style: {
                top: `${subY}px`,
                left: `${subX}px`,
                animationDelay: `${index * 0.05}s`
            }
        };
    });

    return {
      ...item,
      subItems: subItemsWithPositions,
      style: {
        top: `${y}px`,
        left: `${x}px`,
      }
    };
  });
});

const activeItem = computed(() => {
  return activeItemId.value ? menuItems.value.find(item => item.id === activeItemId.value) : null;
});

// --- FIX #1: "GRACE PERIOD" MOUSE EVENT LOGIC ---
const setActiveItem = (item) => {
  cancelClearTimer(); // Cancel any pending close timer when entering a new item
  activeItemId.value = item.id;
};
const startClearTimer = () => {
  leaveTimer = setTimeout(() => {
    activeItemId.value = null;
  }, 200); // 200ms grace period before closing
};
const cancelClearTimer = () => {
  clearTimeout(leaveTimer); // Cancel the timer
};
</script>

<style scoped>
.radial-menu-wrapper {
  position: relative;
  width: 700px; height: 700px;
  display: flex; justify-content: center; align-items: center;
  color: #fff;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  animation: fade-in-menu 0.5s 0.2s both;
}
@keyframes fade-in-menu { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }

.center-info-box {
  position: relative; z-index: 10;
  width: 250px; height: 250px;
  border-radius: 50%;
  background-color: rgba(20, 20, 25, 0.7);
  border: 1px solid rgba(77, 216, 243, 0.5);
  display: flex; flex-direction: column; justify-content: center; align-items: center;
  text-align: center; padding: 20px;
  backdrop-filter: blur(5px);
  pointer-events: none;
}
.center-title { font-size: 1.5rem; font-weight: 600; color: #4DD8F3; margin: 0 0 5px 0; }
.center-description { font-size: 0.9rem; color: #ccc; margin: 0; }

.menu-items-container {
  position: absolute;
  top: 50%; left: 50%;
  width: 0; height: 0;
}

.menu-item, .sub-item {
  position: absolute;
  background-color: rgba(20, 20, 25, 0.7);
  border: 1px solid rgba(77, 216, 243, 0.3);
  backdrop-filter: blur(5px);
  border-radius: 50%;
  display: flex; justify-content: center; align-items: center;
  text-decoration: none;
  color: #fff;
  transition: all 0.3s ease;
}

.menu-item {
  width: 100px; height: 100px;
  margin-top: -50px; margin-left: -50px;
  z-index: 5;
}
.menu-item:hover {
  background-color: rgba(77, 216, 243, 0.8);
  border-color: #fff;
  transform: scale(1.1);
  z-index: 6;
}
.item-content { display: flex; justify-content: center; align-items: center; }
.item-icon { width: 50%; height: 50%; filter: brightness(0) invert(1); }

.sub-item {
  width: 80px; height: 80px;
  margin-top: -40px; margin-left: -40px;
  z-index: 4;
  animation: pop-out 0.3s both;
}
.sub-item:hover {
  background-color: #4DD8F3;
  color: #141419;
  border-color: #fff;
  transform: scale(1.1);
}
.sub-item-label { font-weight: 500; font-size: 0.8rem; text-align: center; padding: 2px;}

.pop-out-enter-active, .pop-out-leave-active { transition: all 0.3s ease; }
.pop-out-enter-from, .pop-out-leave-to { opacity: 0; transform: scale(0.5); }
</style>